import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Dialog, Link, Stack, t, Text, TextField, toast } from '@kanbios/ui';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../lib/api';
import { asName } from '../lib/environments';
import { pendingLabel } from './PendingButton';
import { openPullRequestOf, PullRequestConflict } from './PullRequestConflict';
import { changeRef } from './catalogue/PullRequestNotice';

export type ApplicationRename = maintenance.components['schemas']['ApplicationRename'];

/** As the catalogue accepts it (backend-common APPLICATION_NAME): its deployments add `-<env>`. */
const APPLICATION_NAME = /^[a-z0-9]([a-z0-9-]{0,48}[a-z0-9])?$/;
const MAX = 50;

/**
 * D65 « Renommer » an application, like a server's (D22): the person types the
 * new name (nothing to propose); a pull request renames the application, its
 * deployments `<application>-<env>` and every reference in the catalogue; once
 * merged, the portal applies the project's supervision again and carries its
 * records over to the new name.
 */
export function ApplicationRenameDialog({ rename, onClose, onOpened, onStepUp }: { rename: ApplicationRename; onClose: () => void; onOpened: () => void; onStepUp: () => void }) {
  const [name, setName] = useState(rename.application);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const to = asName(name, MAX, true);
  const invalid = name !== '' && !APPLICATION_NAME.test(to);
  const renamed = rename.deployments.filter(d => d.derived).map(d => `${to}${d.name.slice(rename.application.length)}`);

  const submit = async () => {
    setSending(true);
    setFailure(null);
    try {
      const { pullRequest } = unwrap(await api.maintenance.POST('/applications/{application}/rename', { params: { path: { application: rename.application } }, body: { to } }));
      toast({ tone: 'pos', title: t('rename.opened', { ref: changeRef(pullRequest) }) });
      onOpened();
    } catch (e) {
      if (e instanceof ApiFailure && e.body.code === 'MFA_STEP_UP_REQUIRED') onStepUp();
      else if (e instanceof ApiFailure) setFailure(e);
      else throw e;
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open
      size="md"
      onClose={onClose}
      title={t('applicationRename.title', { application: rename.application })}
      description={t('applicationRename.lead')}
      confirmLabel={pendingLabel(t('applicationRename.confirm'), sending)}
      confirmDisabled={!to || invalid || to === rename.application || sending}
      onConfirm={() => void submit()}
    >
      <Stack gap={16}>
        <TextField
          label={t('rename.name')}
          value={name}
          mono
          // Formatted as typed: lower case, no accent, a dash for any other character (« API Factures » → api-factures).
          onChange={(e: { target: { value: string } }) => { setName(asName(e.target.value, MAX)); setFailure(null); }}
          hint={t('applicationRename.hint')}
          error={invalid ? t('applicationRename.invalid') : undefined}
          required
        />
        {renamed.length > 0 && to !== rename.application && !invalid && (
          <Text variant="body-sm" tone="mute">{t('applicationRename.deployments', { names: renamed.join(', ') })}</Text>
        )}
        <Stack gap={4}>
          <Text variant="body-sm">{t('rename.after.title')}</Text>
          <Text variant="body-sm" tone="mute">{t('applicationRename.after.catalogue')}</Text>
          <Text variant="body-sm" tone="mute">{t('applicationRename.after.monitoring')}</Text>
          <Text variant="body-sm" tone="mute">{t('applicationRename.after.records')}</Text>
        </Stack>
        <Alert tone="info">{t('applicationRename.repository')}</Alert>
        {failure && openPullRequestOf(failure) ? (
          <PullRequestConflict failure={failure} onClosed={() => setFailure(null)} />
        ) : failure && (
          <Alert tone="neg" title={failure.body.message}>{failure.body.hint}</Alert>
        )}
      </Stack>
    </Dialog>
  );
}

/** Where the rename stands, from its pull request to the new name read in the catalogue (and the page under it). */
export function ApplicationRenameProgress({ rename }: { rename: ApplicationRename }) {
  const navigate = useNavigate();
  const pr = rename.pullRequest;
  if (!pr || pr.state === 'closed') return null;
  const link = <Link href={pr.url} external>{t('pullRequest.link', { ref: changeRef(pr) })}</Link>;
  const href = `/applications/${encodeURIComponent(pr.to)}`;
  const newPage = (
    <Link href={href} onClick={(e: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean }) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(href); }}>
      {t('rename.progress.openNew', { to: pr.to })}
    </Link>
  );
  if (pr.state === 'open' || pr.state === 'validation_failed') {
    return (
      <Alert tone={pr.state === 'open' ? 'info' : 'warn'} title={t(pr.state === 'open' && pr.autoMerge ? 'rename.progress.autoMerge' : `rename.progress.${pr.state}`, { to: pr.to })}>
        <Stack gap={4}>{pr.ciMessage && <Text variant="body-sm">{pr.ciMessage}</Text>}{link}</Stack>
      </Alert>
    );
  }
  if (pr.state === 'applied' || pr.executed) {
    return (
      <Alert tone="pos" title={t('applicationRename.progress.applied', { to: pr.to })}>
        {newPage}
      </Alert>
    );
  }
  // Merged: the catalogue reads the new name, then the portal's part runs.
  const failed = [
    ...(pr.steps?.monitoring ? [t('applicationRename.progress.monitoring', { error: pr.steps.monitoring })] : []),
    ...(pr.steps?.migrations ?? []).filter(m => m.error).map(m => t('rename.progress.part', { part: m.part, error: m.error ?? '' })),
  ];
  return (
    <Alert tone={failed.length ? 'warn' : 'info'} title={t(failed.length ? 'rename.progress.failed' : 'applicationRename.progress.merged', { to: pr.to })}>
      <Stack gap={4}>
        {failed.map(f => <Text key={f} variant="body-sm">{f}</Text>)}
        {failed.length > 0 && <Text variant="body-sm" tone="mute">{t('rename.progress.retry')}</Text>}
        {link}
      </Stack>
    </Alert>
  );
}
